import React from "react";
import { X, FileText, CheckCircle, User, Calendar, Clock } from "lucide-react";

const SessionNotesPopup = ({ open, onClose, appointment, details, loading }) => {
  if (!open) return null;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "N/A";
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? "PM" : "AM";
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    } catch {
      return timeString;
    }
  };

  // Get appointment data from API response
  const appointmentData = details?.data?.[0] || appointment;
  const paymentDetails = details?.appointmentPaymentDetails?.[0];
  const statusHistory = details?.statusHistory || [];

  // Get latest status
  const getLatestStatus = () => {
    if (statusHistory.length > 0) {
      const sorted = [...statusHistory].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );
      return sorted[0];
    }
    return null;
  };

  const latestStatus = getLatestStatus();

  // Get status display
  const getStatusDisplay = (status) => {
    const statusMap = {
      0: { text: "Upcoming", color: "#FF8C00", bg: "#FFF6E5" },
      1: { text: "Confirmed", color: "#3A7AFE", bg: "#E5F0FF" },
      2: { text: "Completed", color: "#18B58A", bg: "#E8F8ED" },
      3: { text: "Cancelled", color: "#FF4444", bg: "#FFE8E8" },
    };
    return statusMap[status] || { text: "Unknown", color: "#999", bg: "#F5F5F5" };
  };

  const statusDisplay = getStatusDisplay(appointmentData?.status);

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-[500px] max-h-[90vh] rounded-lg shadow-xl overflow-hidden font-lato">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-[#e8ece8]">
          <h2 className="text-[20px] font-semibold text-[#22302b] flex items-center gap-2">
            <FileText size={20} className="text-[#57b26a]" />
            Session Notes
          </h2>
          <button 
            onClick={onClose}
            className="hover:bg-[#f5f5f5] rounded-full p-1 transition"
          >
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto" style={{ maxHeight: "calc(90vh - 130px)" }}>
          {/* Patient Info Card */}
          <div className="bg-[#f7f9f7] rounded-lg p-4 flex items-center justify-between mb-5">
            <div className="flex gap-3 items-center">
              <div className="w-12 h-12 rounded-full bg-[#57b26a] flex items-center justify-center text-white font-semibold text-xl">
                {appointmentData?.name?.charAt(0)?.toUpperCase() || "?"}
              </div>
              <div>
                <p className="text-[18px] font-semibold text-[#22302b]">
                  {appointmentData?.name || "Patient"}
                </p>
                <div className="flex items-center gap-3 text-[12px] text-[#98a19b] mt-1">
                  <span className="flex items-center gap-1">
                    <Calendar size={12} />
                    {formatDate(appointmentData?.appointment_date)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {formatTime(appointmentData?.appointment_time)}
                  </span>
                </div>
              </div>
            </div>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
              <FileText size={15} className="text-[#57b26a]" />
            </div>
          </div>

          {/* Session Details Grid */}
          <div className="grid grid-cols-2 gap-4 mb-5">
            <div className="bg-[#fafcfa] rounded-lg p-3">
              <p className="text-[13px] text-[#98a19b] uppercase tracking-wide">Consultation Mode</p>
              <p className="text-[12px] font-medium text-[#22302b] mt-1">
                {appointmentData?.mode_consultation || "N/A"}
              </p>
            </div>
            <div className="bg-[#fafcfa] rounded-lg p-3">
              <p className="text-[12px] text-[#98a19b] uppercase tracking-wide">Status</p>
              <span 
                className="inline-block px-3 py-1 rounded-full text-[12px] font-medium mt-1"
                style={{ 
                  backgroundColor: statusDisplay.bg, 
                  color: statusDisplay.color 
                }}
              >
                {statusDisplay.text}
              </span>
            </div>
            <div className="bg-[#fafcfa] rounded-lg p-3">
              <p className="text-[13px] text-[#98a19b] uppercase tracking-wide">Payment Status</p>
              <p className="text-[12px] font-medium text-[#22302b] mt-1 capitalize">
                {paymentDetails?.status || appointmentData?.payment_status || "N/A"}
              </p>
            </div>
            <div className="bg-[#fafcfa] rounded-lg p-3">
              <p className="text-[13px] text-[#98a19b] uppercase tracking-wide">Follow-up Status</p>
              <p className="text-[12px] font-medium text-[#22302b] mt-1 capitalize">
                {appointmentData?.followup_status || "N/A"}
              </p>
            </div>
          </div>

          {/* Purpose */}
          {appointmentData?.purpose && (
            <div className="mb-5">
              <h3 className="text-[13px] font-semibold uppercase tracking-wide text-[#98a19b] mb-2">Purpose</h3>
              <p className="text-[12px] text-[#555] bg-[#f7f9f7] rounded-lg p-3">
                {appointmentData.purpose}
              </p>
            </div>
          )}

          {/* Notes / Comments */}
          {appointmentData?.comments && (
            <div className="mb-5">
              <h3 className="text-[13px] font-semibold text-[#22302b] mb-2 flex items-center gap-2">
                <FileText size={14} className="text-[#57b26a]" />
                Consultation Notes
              </h3>
              <p className="text-[13px] text-[#555] bg-[#f7f9f7] rounded-lg p-3">
                {appointmentData.comments}
              </p>
            </div>
          )}

          {/* Payment Details */}
          {paymentDetails && (
            <div className="mb-5">
              <h3 className="text-[13px] font-semibold text-[#22302b] mb-2">Payment Details</h3>
              <div className="bg-[#f7f9f7] rounded-lg p-3 text-[13px]">
                <div className="flex justify-between py-1">
                  <span className="text-[#98a19b]">Amount</span>
                  <span className="font-medium text-[#22302b]">
                    ₹{(paymentDetails.amount / 100).toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between py-1 border-t border-[#e8ece8] mt-1 pt-1">
                  <span className="text-[#98a19b]">Order ID</span>
                  <span className="text-[#22302b]">{paymentDetails.order_id}</span>
                </div>
              </div>
            </div>
          )}

          {/* Status History */}
          {statusHistory.length > 0 && (
            <div>
              <h3 className="text-[13px] font-semibold text-[#22302b] mb-2 flex items-center gap-2">
                <CheckCircle size={14} className="text-[#57b26a]" />
                Status History
              </h3>
              <div className="space-y-2 max-h-[120px] overflow-y-auto pr-2">
                {statusHistory.map((item) => (
                  <div key={item.id} className="bg-[#fafcfa] rounded-lg p-2 text-[12px]">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-[#22302b]">
                        {item.status || "Updated"}
                      </span>
                      <span className="text-[#98a19b] text-[11px]">
                        {formatDate(item.createdAt)}
                      </span>
                    </div>
                    {item.user?.name && (
                      <div className="text-[11px] text-[#98a19b] mt-1">
                        by {item.user.name}
                      </div>
                    )}
                    {item.notes && (
                      <div className="text-[12px] text-[#555] mt-1">
                        {item.notes}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionNotesPopup;